import { useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: Convex generated API lives outside src/; no @convex/ path alias in tsconfig — all hooks in src/hooks/ use this relative path
import type { IOrgContext } from '@/@types/organisation';

/**
 * Provides organisation context for the current user.
 *
 * - If user is in an org, returns org details and org's industry as effectiveIndustry
 * - If user is not in an org, returns personal business industry as effectiveIndustry
 * - Navigation and data queries use effectiveIndustry to determine what to show
 */
export function useOrgContext(): IOrgContext {
  const currentOrg = useQuery(api.organisations.queries.getCurrentOrg);
  const currentMembership = useQuery(api.organisations.queries.getCurrentMembership);
  const currentBusiness = useQuery(api.businesses.queries.getCurrentBusiness);

  return useMemo(() => {
    // Still loading
    const isLoading =
      currentOrg === undefined || currentMembership === undefined || currentBusiness === undefined;

    if (isLoading) {
      return {
        currentOrg: null,
        effectiveIndustry: '',
        isOrgOwner: false,
        isInOrg: false,
        isLoading: true,
      };
    }

    // User is in an org
    if (currentOrg) {
      const effectiveIndustry =
        currentOrg.industry === 'other'
          ? currentOrg.customIndustry || 'other'
          : currentOrg.industry;

      return {
        currentOrg,
        effectiveIndustry,
        isOrgOwner: currentMembership?.role === 'owner',
        isInOrg: true,
        isLoading: false,
      };
    }

    // User is not in an org — use personal business industry
    const personalIndustry = currentBusiness?.industry || '';

    return {
      currentOrg: null,
      effectiveIndustry: personalIndustry,
      isOrgOwner: false,
      isInOrg: false,
      isLoading: false,
    };
  }, [currentOrg, currentMembership, currentBusiness]);
}
